/**
 * Document Text Extractor
 * Extracts plain text from DOCX, PDF, and PPTX files client-side.
 * Used by portfolio AI features to send document content to the AI proxy.
 */

import mammoth from 'mammoth/mammoth.browser';
import JSZip from 'jszip';

// ─────────────── DOCX ───────────────

/**
 * Extract text from a DOCX file using mammoth.
 * @param {File} file
 * @returns {Promise<string>}
 */
const extractTextFromDocx = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return (result.value || '').trim();
};

// ─────────────── PDF ───────────────

let _pdfjsLibCached = null;
let _pdfjsWorkerReady = false;

/**
 * Initialize pdfjs-dist with robust worker fallbacks.
 * Tries multiple CDN sources and local worker file.
 */
const initPdfJs = async () => {
  if (_pdfjsLibCached && _pdfjsWorkerReady) return _pdfjsLibCached;

  const mod = await import('pdfjs-dist');
  const lib = mod.default || mod;

  if (typeof window !== 'undefined' && lib.GlobalWorkerOptions && !_pdfjsWorkerReady) {
    const workerPaths = [
      'https://unpkg.com/pdfjs-dist@5.4.530/build/pdf.worker.min.mjs',
      'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.530/build/pdf.worker.min.mjs',
      '/pdf.worker.min.mjs'
    ];

    for (const wp of workerPaths) {
      try {
        lib.GlobalWorkerOptions.workerSrc = wp;
        console.log('[PDF] Worker set to:', wp);
        break;
      } catch (e) {
        console.warn('[PDF] Worker path failed:', wp, e);
      }
    }
    _pdfjsWorkerReady = true;
  }

  _pdfjsLibCached = lib;
  return lib;
};

/**
 * Extract text from a PDF file using pdfjs-dist with robust fallbacks.
 * Groups text items by Y-position to preserve line structure.
 * Falls back to the full pdfParser.js if the simple extraction returns empty.
 * @param {File} file
 * @returns {Promise<string>}
 */
const extractTextFromPdf = async (file) => {
  try {
    const lib = await initPdfJs();
    const arrayBuffer = await file.arrayBuffer();

    let doc;
    try {
      doc = await lib.getDocument({ data: new Uint8Array(arrayBuffer) }).promise;
    } catch (loadErr) {
      console.warn('[PDF] First load attempt failed, retrying with different options:', loadErr.message);
      doc = await lib.getDocument({
        data: new Uint8Array(arrayBuffer),
        useSystemFonts: true,
        disableFontFace: true
      }).promise;
    }

    const pages = [];
    const maxPages = Math.min(doc.numPages, 20);

    for (let i = 1; i <= maxPages; i++) {
      try {
        const page = await doc.getPage(i);
        const content = await page.getTextContent();

        // Group text items by Y-position to preserve line structure
        const lines = {};
        for (const item of content.items) {
          if (!item.str || !item.str.trim()) continue;
          const y = Math.round(item.transform?.[5] || 0);
          if (!lines[y]) lines[y] = [];
          lines[y].push(item.str);
        }

        // Sort by Y-position (descending = top of page first) and join
        const sortedYs = Object.keys(lines).map(Number).sort((a, b) => b - a);
        const pageText = sortedYs.map(y => lines[y].join(' ')).join('\n');
        if (pageText.trim()) pages.push(pageText);
      } catch (pageErr) {
        console.warn(`[PDF] Failed to extract page ${i}:`, pageErr.message);
      }
    }

    const text = pages.join('\n\n').trim();

    // If primary extraction returned very little, try the robust pdfParser as fallback
    if (text.length < 20) {
      console.warn('[PDF] Primary extraction returned minimal text, trying pdfParser fallback...');
      try {
        const { extractTextFromPDF } = await import('../services/pdfParser.js');
        if (extractTextFromPDF) {
          const fallbackResult = await extractTextFromPDF(file);
          if (typeof fallbackResult === 'string' && fallbackResult.length > text.length) {
            return fallbackResult;
          }
        }
      } catch (fallbackErr) {
        console.warn('[PDF] pdfParser fallback also failed:', fallbackErr.message);
      }
    }

    return text;
  } catch (err) {
    console.error('[PDF] extractTextFromPdf failed:', err);
    // Last resort: try the full pdfParser module
    try {
      const { extractTextFromPDF } = await import('../services/pdfParser.js');
      if (extractTextFromPDF) {
        return await extractTextFromPDF(file);
      }
    } catch (fallbackErr) {
      console.error('[PDF] pdfParser fallback also failed:', fallbackErr.message);
    }
    return '';
  }
};

// ─────────────── PPTX ───────────────

/**
 * Extract text from a PPTX file by parsing the XML inside the ZIP.
 * @param {File} file
 * @returns {Promise<string>}
 */
const extractTextFromPptx = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(arrayBuffer);

  // Find all slide XML files (ppt/slides/slide1.xml, slide2.xml, ...)
  const slideFiles = Object.keys(zip.files)
    .filter((name) => /^ppt\/slides\/slide\d+\.xml$/i.test(name))
    .sort((a, b) => {
      const numA = parseInt(a.match(/slide(\d+)/i)[1], 10);
      const numB = parseInt(b.match(/slide(\d+)/i)[1], 10);
      return numA - numB;
    });

  const slideTexts = [];

  for (const slideFile of slideFiles) {
    const xmlStr = await zip.files[slideFile].async('string');
    // Extract text from <a:t> tags (PowerPoint text runs)
    const texts = [];
    const regex = /<a:t[^>]*>([\s\S]*?)<\/a:t>/gi;
    let match;
    while ((match = regex.exec(xmlStr)) !== null) {
      const text = match[1].trim();
      if (text) texts.push(text);
    }
    if (texts.length > 0) {
      slideTexts.push(texts.join(' '));
    }
  }

  return slideTexts.join('\n\n').trim();
};

// ─────────────── Excel (basic) ───────────────

/**
 * Extract text from an Excel file by parsing sharedStrings.xml inside the ZIP.
 * This captures all string cell values but not formulas or numbers.
 * @param {File} file
 * @returns {Promise<string>}
 */
const extractTextFromExcel = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(arrayBuffer);

  const sharedStrings = zip.files['xl/sharedStrings.xml'];
  if (!sharedStrings) return '';

  const xmlStr = await sharedStrings.async('string');
  const texts = [];
  const regex = /<t[^>]*>([\s\S]*?)<\/t>/gi;
  let match;
  while ((match = regex.exec(xmlStr)) !== null) {
    const text = match[1].trim();
    if (text) texts.push(text);
  }

  return texts.join(' ').trim();
};

// ─────────────── Router + Truncation ───────────────

/**
 * Smart truncation that prefers paragraph boundaries.
 * @param {string} text
 * @param {number} maxChars
 * @returns {string}
 */
export const truncateForAI = (text, maxChars = 3000) => {
  if (!text || text.length <= maxChars) return text;

  // Try to cut at a paragraph boundary
  const truncated = text.slice(0, maxChars);
  const lastParagraph = truncated.lastIndexOf('\n\n');
  if (lastParagraph > maxChars * 0.6) {
    return truncated.slice(0, lastParagraph).trim() + '\n\n[Document truncated...]';
  }

  // Fall back to sentence boundary
  const lastSentence = truncated.lastIndexOf('. ');
  if (lastSentence > maxChars * 0.6) {
    return truncated.slice(0, lastSentence + 1).trim() + '\n\n[Document truncated...]';
  }

  return truncated.trim() + '... [Document truncated]';
};

/**
 * Extract text from a file based on its extension/type.
 * @param {File} file - The File object to extract text from.
 * @returns {Promise<string>} Extracted plain text (may be empty for unsupported types).
 */
export const extractTextFromFile = async (file) => {
  const name = (file.name || '').toLowerCase();
  const ext = name.split('.').pop();

  try {
    if (['docx', 'doc'].includes(ext)) {
      return await extractTextFromDocx(file);
    }
    if (ext === 'pdf') {
      return await extractTextFromPdf(file);
    }
    if (['pptx', 'ppt'].includes(ext)) {
      return await extractTextFromPptx(file);
    }
    if (['xlsx', 'xls'].includes(ext)) {
      return await extractTextFromExcel(file);
    }
    // Plain text fallback
    if (['txt', 'md', 'csv', 'json'].includes(ext)) {
      return await file.text();
    }
    return '';
  } catch (err) {
    console.error(`[DocumentTextExtractor] Failed to extract text from ${file.name}:`, err);
    return '';
  }
};

export default {
  extractTextFromFile,
  truncateForAI
};
